import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Search, UserCircle, Phone, Mail, Eye, Edit, Trash2, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';

const Clientes = () => {
  const navigate = useNavigate();
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [ordenacao, setOrdenacao] = useState('recente');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCliente, setEditingCliente] = useState(null);
  const [formData, setFormData] = useState({
    nome_completo: '',
    cpf_cnpj: '',
    rg: '',
    profissao: '',
    estado_civil: '',
    telefone_principal: '',
    telefone_secundario: '',
    email: '',
    cep: '',
    endereco: '',
    cidade: '',
    estado: '',
    observacoes: ''
  });

  useEffect(() => {
    loadClientes();
  }, []);

  const loadClientes = async () => {
    try {
      const response = await api.getClientes();
      setClientes(response.data);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
      toast.error('Erro ao carregar clientes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Limpar valores vazios antes de enviar (converter '' para null)
      const cleanedData = Object.fromEntries(
        Object.entries(formData).map(([key, value]) => [
          key,
          value === '' ? null : value
        ])
      );

      if (editingCliente) {
        await api.updateCliente(editingCliente.id, cleanedData);
        toast.success('Cliente atualizado com sucesso!');
      } else {
        await api.createCliente(cleanedData);
        toast.success('Cliente cadastrado com sucesso!');
      }
      setIsDialogOpen(false);
      setEditingCliente(null);
      setFormData({
        nome_completo: '',
        cpf_cnpj: '',
        rg: '',
        profissao: '',
        estado_civil: '',
        telefone_principal: '',
        telefone_secundario: '',
        email: '',
        cep: '',
        endereco: '',
        cidade: '',
        estado: '',
        observacoes: ''
      });
      loadClientes();
    } catch (error) {
      console.error('Erro ao salvar cliente:', error);
      toast.error('Erro ao salvar cliente');
    }
  };

  const handleEdit = (cliente) => {
    setEditingCliente(cliente);
    setFormData({
      nome_completo: cliente.nome_completo,
      cpf_cnpj: cliente.cpf_cnpj || '',
      rg: cliente.rg || '',
      profissao: cliente.profissao || '',
      estado_civil: cliente.estado_civil || '',
      telefone_principal: cliente.telefone_principal || '',
      telefone_secundario: cliente.telefone_secundario || '',
      email: cliente.email || '',
      cep: cliente.cep || '',
      endereco: cliente.endereco || '',
      cidade: cliente.cidade || '',
      estado: cliente.estado || '',
      observacoes: cliente.observacoes || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (clienteId) => {
    if (!window.confirm('Deseja realmente excluir este cliente?')) return;

    try {
      await api.deleteCliente(clienteId);
      toast.success('Cliente excluído com sucesso!');
      loadClientes();
    } catch (error) {
      console.error('Erro ao excluir cliente:', error);
      toast.error('Erro ao excluir cliente');
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingCliente(null);
    setFormData({
      nome_completo: '',
      cpf_cnpj: '',
      rg: '',
      profissao: '',
      estado_civil: '',
      telefone_principal: '',
      telefone_secundario: '',
      email: '',
      cep: '',
      endereco: '',
      cidade: '',
      estado: '',
      observacoes: ''
    });
  };

  const filteredClientes = useMemo(() => {
    let result = clientes.filter(cliente =>
      cliente.nome_completo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cliente.cpf_cnpj?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cliente.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Ordenação
    switch (ordenacao) {
      case 'nome_asc':
        result.sort((a, b) => a.nome_completo.localeCompare(b.nome_completo, 'pt-BR'));
        break;
      case 'nome_desc':
        result.sort((a, b) => b.nome_completo.localeCompare(a.nome_completo, 'pt-BR'));
        break;
      case 'antigo':
        result.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
        break;
      case 'recente':
      default:
        result.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        break;
    }

    return result;
  }, [clientes, searchTerm, ordenacao]);

  if (loading) {
    return <div className="p-4 sm:p-8" data-testid="clientes-loading">Carregando...</div>;
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="clientes-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900">Clientes</h1>
          <p className="text-slate-600 mt-1 text-sm sm:text-base">Gerencie seus clientes</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800 w-full sm:w-auto" data-testid="add-cliente-button">
              <Plus className="w-4 h-4 mr-2" />
              Novo Cliente
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingCliente ? 'Editar Cliente' : 'Cadastrar Novo Cliente'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="nome_completo">Nome Completo *</Label>
                  <Input
                    id="nome_completo"
                    data-testid="cliente-nome-input"
                    required
                    value={formData.nome_completo}
                    onChange={(e) => setFormData({ ...formData, nome_completo: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="cpf_cnpj">CPF/CNPJ</Label>
                  <Input
                    id="cpf_cnpj"
                    data-testid="cliente-cpf-input"
                    value={formData.cpf_cnpj}
                    onChange={(e) => setFormData({ ...formData, cpf_cnpj: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="rg">RG</Label>
                  <Input
                    id="rg"
                    data-testid="cliente-rg-input"
                    value={formData.rg}
                    onChange={(e) => setFormData({ ...formData, rg: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="profissao">Profissão</Label>
                  <Input
                    id="profissao"
                    data-testid="cliente-profissao-input"
                    value={formData.profissao}
                    onChange={(e) => setFormData({ ...formData, profissao: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="estado_civil">Estado Civil</Label>
                  <Select
                    value={formData.estado_civil}
                    onValueChange={(value) => setFormData({ ...formData, estado_civil: value })}
                  >
                    <SelectTrigger data-testid="cliente-estado-civil-select">
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Solteiro(a)">Solteiro(a)</SelectItem>
                      <SelectItem value="Casado(a)">Casado(a)</SelectItem>
                      <SelectItem value="Divorciado(a)">Divorciado(a)</SelectItem>
                      <SelectItem value="Viúvo(a)">Viúvo(a)</SelectItem>
                      <SelectItem value="União Estável">União Estável</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="email">E-mail</Label>
                  <Input
                    id="email"
                    data-testid="cliente-email-input"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="telefone_principal">Telefone Principal</Label>
                  <Input
                    id="telefone_principal"
                    data-testid="cliente-telefone-input"
                    value={formData.telefone_principal}
                    onChange={(e) => setFormData({ ...formData, telefone_principal: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="telefone_secundario">Telefone Secundário</Label>
                  <Input
                    id="telefone_secundario"
                    value={formData.telefone_secundario}
                    onChange={(e) => setFormData({ ...formData, telefone_secundario: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="cep">CEP</Label>
                  <Input
                    id="cep"
                    value={formData.cep}
                    onChange={(e) => setFormData({ ...formData, cep: e.target.value })}
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="endereco">Endereço</Label>
                  <Input
                    id="endereco"
                    value={formData.endereco}
                    onChange={(e) => setFormData({ ...formData, endereco: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="cidade">Cidade</Label>
                  <Input
                    id="cidade"
                    value={formData.cidade}
                    onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="estado">Estado</Label>
                  <Input
                    id="estado"
                    maxLength={2}
                    value={formData.estado}
                    onChange={(e) => setFormData({ ...formData, estado: e.target.value.toUpperCase() })}
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="observacoes">Observações</Label>
                  <textarea
                    id="observacoes"
                    className="w-full min-h-[100px] p-2 border rounded-md"
                    value={formData.observacoes}
                    onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={handleCloseDialog}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-cliente-button">
                  {editingCliente ? 'Atualizar' : 'Cadastrar'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Buscar clientes..."
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="search-clientes-input"
          />
        </div>
        <div className="w-full sm:w-52">
          <Select value={ordenacao} onValueChange={setOrdenacao}>
            <SelectTrigger data-testid="ordenacao-clientes-select">
              <ArrowUpDown className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Ordenar por" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="recente">Mais recente</SelectItem>
              <SelectItem value="antigo">Mais antigo</SelectItem>
              <SelectItem value="nome_asc">Nome (A-Z)</SelectItem>
              <SelectItem value="nome_desc">Nome (Z-A)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {filteredClientes.length > 0 ? (
          filteredClientes.map((cliente) => (
            <Card key={cliente.id} className="hover:shadow-lg transition-shadow" data-testid="cliente-card">
              <CardHeader className="p-4 sm:p-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <UserCircle className="w-6 h-6 sm:w-8 sm:h-8 text-slate-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-base sm:text-lg truncate">{cliente.nome_completo}</CardTitle>
                    {cliente.cpf_cnpj && (
                      <p className="text-xs sm:text-sm text-slate-600 mt-1">{cliente.cpf_cnpj}</p>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-2 p-4 sm:p-6 pt-0">
                {cliente.telefone_principal && (
                  <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-600">
                    <Phone className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{cliente.telefone_principal}</span>
                  </div>
                )}
                {cliente.email && (
                  <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-600">
                    <Mail className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{cliente.email}</span>
                  </div>
                )}
                {cliente.cidade && cliente.estado && (
                  <p className="text-xs sm:text-sm text-slate-600">
                    {cliente.cidade}/{cliente.estado}
                  </p>
                )}
                <div className="flex gap-2 mt-3">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1 text-xs sm:text-sm"
                    onClick={() => navigate(`/clientes/${cliente.id}`)}
                    data-testid="view-cliente-button"
                  >
                    <Eye className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Ver Detalhes</span>
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleEdit(cliente)}
                    data-testid="edit-cliente-button"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleDelete(cliente.id)}
                    data-testid="delete-cliente-button"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <div className="col-span-full text-center py-12 text-slate-500">
            Nenhum cliente encontrado
          </div>
        )}
      </div>
    </div>
  );
};

export default Clientes;